import React, { createContext, useEffect, useState, useCallback } from "react";
import SplashScreen from "src/components/SplashScreen";
import { FETCH_AUTH_USER_QUERY, SIGN_IN_USER_MUTATION } from "src/constants/graphql";
import { useQuery } from "@apollo/client/react";
import { useLocation } from "react-router";
import useMutation from "src/hooks/useMutation";
import { client as apolloClient } from "src/share/ApolloProvider";
import { getReturnToFromLocation } from "../utils/authReturnTo";
import { ADMINISTRATE_PATH, isAdministrateWorkspace } from "../utils/workspaceDisplay";
import http from "../utils/http";
import { stopIntercom } from "../utils/intercom";

const initialState = {
  isAuthenticated: false,
  user: null,
  loading: true,
};

const AuthContext = createContext({
  ...initialState,
  login: () => Promise.resolve(),
  logout: () => {},
  register: () => Promise.resolve(),
});

export const AuthProvider = ({ children }) => {
  const [signInUser] = useMutation(SIGN_IN_USER_MUTATION);
  const [state, setState] = useState(initialState);
  const location = useLocation();

  // Stable identity: hooks (useSelectionContext) list it as a dependency, and a
  // new function per provider render would re-fire their effects.
  const setUser = useCallback((user) => {
    if (!user) return;
    setState({ user: user, isAuthenticated: true });
  }, []);

  const login = (
    email,
    password,
    { onSuccess, onFailure, onWorkspaceSelection, remember = false, companyId }
  ) => {
    const returnTo = getReturnToFromLocation(location);

    return signInUser({
      variables: { email, password, remember, companyId },
      onSuccess: async ({ user, workspaces }) => {
        if (!user && workspaces?.length) {
          if (onWorkspaceSelection) onWorkspaceSelection(workspaces);
          return;
        }

        const subdomain = window.location.hostname.split(".")[0];
        const isLocalHost = ["localhost", "127.0.0.1"].includes(window.location.hostname);
        if (isLocalHost || subdomain === user.company.subdomain) {
          if (isAdministrateWorkspace(user.company)) {
            window.location.replace(ADMINISTRATE_PATH);
            return;
          }
          // Same subdomain — the backend already set the httpOnly cookie in the sign-in response.
          await apolloClient.clearStore();
          setUser(user);
          if (onSuccess) onSuccess(user);
          return;
        } else {
          // Cross-subdomain — redirect through the backend /auth/token bridge which plants the
          // cookie on the target subdomain and then redirects to /login.
          const host = [user.company.subdomain, ...window.location.host.split(".").slice(1)].join(
            "."
          );
          const protocol = window.location.protocol;
          const authTokenUrl = new URL(`${protocol}//${host}/auth/token`);
          authTokenUrl.searchParams.set("token", user.token);
          const destination =
            returnTo || (isAdministrateWorkspace(user.company) ? ADMINISTRATE_PATH : null);
          if (destination) {
            authTokenUrl.searchParams.set("returnTo", destination);
          }
          window.location.href = authTokenUrl.toString();
        }
      },
      onFailure,
    });
  };

  const logout = async () => {
    stopIntercom();
    try {
      await http.post("/auth/logout");
    } catch (e) {
      // Best-effort — even if the request fails, clear local React state below.
    }
    setState({ isAuthenticated: false, user: null, loading: false });
    apolloClient.clearStore();
  };

  const { loading, data: { user } = {}, refetch } = useQuery(FETCH_AUTH_USER_QUERY);

  useEffect(() => {
    if (loading) return;

    if (user) {
      setUser(user);
    } else {
      setState({ isAuthenticated: false, user: null });
    }
  }, [user, loading]);

  if (loading || (user && !state.isAuthenticated)) {
    return <SplashScreen />;
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        company: state?.user?.company,
        login,
        logout,
        setUser,
        refetch,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
